import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { Box, Skeleton, Surface, Text } from '@/ui/primitives';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ElementType, ReactElement, ReactNode } from 'react';

type StatusMetricCardsGridProps = {
  metricCards: MetricCardItem[];
  loading?: boolean;
  skeletonCount?: number;
  comparisonWindowLabel: string | null;
  resolveStatusCardIcon: (statusId: string) => ElementType;
  resolveStatusCardIconColor: (statusTone: MetricCardItem['statusTone']) => string;
};

const renderStatusCardDeltaText = (
  value: MetricCardItem['countDelta'],
  tone: MetricCardItem['countDeltaTone'],
  wrapInBrackets = false,
): ReactElement | null => {
  if (value === null || value === undefined || value === '') return null;
  const normalizedValue =
    typeof value === 'string' && wrapInBrackets ? value.replace(/^[+-]/, '') : value;
  if (normalizedValue === '') return null;
  const content =
    typeof normalizedValue === 'string' || typeof normalizedValue === 'number'
      ? wrapInBrackets && normalizedValue !== '-'
        ? `(${normalizedValue})`
        : normalizedValue
      : normalizedValue;

  return (
    <Text
      role="bodyMuted"
      sx={(muiTheme) => ({
        color:
          tone === 'positive'
            ? 'success.main'
            : tone === 'negative'
              ? 'error.main'
              : 'text.secondary',
        fontSize: muiTheme.typography.subtitle1.fontSize,
        lineHeight: muiTheme.typography.subtitle1.lineHeight,
        whiteSpace: 'nowrap',
      })}
    >
      {content}
    </Text>
  );
};

const MetricValueText = ({
  children,
  nowrap = false,
}: {
  children: ReactNode;
  nowrap?: boolean;
}): ReactElement => (
  <Text
    role="bodyMuted"
    sx={(muiTheme) => ({
      fontSize: muiTheme.typography.subtitle1.fontSize,
      lineHeight: muiTheme.typography.subtitle1.lineHeight,
      whiteSpace: nowrap ? 'nowrap' : undefined,
    })}
  >
    {children}
  </Text>
);

export const StatusMetricCardsGrid = ({
  metricCards,
  loading = false,
  skeletonCount,
  comparisonWindowLabel,
  resolveStatusCardIcon,
  resolveStatusCardIconColor,
}: StatusMetricCardsGridProps): ReactElement => {
  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
          columnGap: s('xl'),
          rowGap: s('xl'),
        }}
      >
        {Array.from({
          length: skeletonCount ?? Math.max(metricCards.length, 6),
        }).map((_, index) => (
          <Surface
            key={`metric-card-skeleton-${index}`}
            padding="lg"
            sx={{
              backgroundColor: 'background.paper',
              display: 'flex',
              alignItems: 'stretch',
              minWidth: 0,
              px: s('lg'),
              pt: s('md'),
              pb: s('lg'),
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: s('lg'),
                pt: s('md'),
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                }}
              >
                <Skeleton
                  variant="rounded"
                  width="50%"
                  height={componentSizes.dataGrid.skeletonHeight}
                />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: s('sm'),
                    minWidth: 0,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={92}
                    height={componentSizes.dataGrid.skeletonHeight}
                  />
                </Box>
                <Box />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  columnGap: s('lg'),
                  minWidth: 0,
                  pb: s('xs'),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: s('sm'),
                    minWidth: 0,
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={82}
                    height={componentSizes.dataGrid.skeletonHeight}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    justifySelf: 'end',
                    textAlign: 'right',
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={80}
                    height={componentSizes.dataGrid.skeletonHeight}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                }}
              >
                <Skeleton variant="circular" width={28} height={28} />
              </Box>
            </Box>
          </Surface>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        columnGap: s('xl'),
        rowGap: s('xl'),
      }}
    >
      {metricCards.map((card) => {
        const countDelta = card.countDelta ?? card.delta;
        const countDeltaTone = card.countDeltaTone ?? card.deltaTone;
        const hasCountDelta =
          countDelta !== null &&
          countDelta !== undefined &&
          countDelta !== '' &&
          countDelta !== '-';
        const hasPercentageDelta =
          card.percentageDelta !== null &&
          card.percentageDelta !== undefined &&
          card.percentageDelta !== '' &&
          card.percentageDelta !== '-';
        const hasDelta = hasCountDelta || hasPercentageDelta;
        const resolvedDeltaTone = countDeltaTone ?? card.percentageDeltaTone;

        return (
          <Surface
            key={card.id}
            padding="lg"
            sx={{
              backgroundColor: 'background.paper',
              height: '100%',
              display: 'flex',
              alignItems: 'stretch',
              minWidth: 0,
              px: s('lg'),
              pt: s('md'),
              pb: hasDelta ? s('lg') : s('md'),
            }}
          >
            <Box
              sx={{
                width: '100%',
                minWidth: 0,
                display: 'grid',
                gridTemplateRows: hasDelta ? 'auto auto auto' : 'auto auto',
                rowGap: s('none'),
                alignItems: 'center',
                alignContent: 'start',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  columnGap: s('xxs'),
                  pl: s('xs'),
                  minWidth: 0,
                }}
              >
                <Text
                  role="label"
                  sx={(muiTheme) => ({
                    color: 'text.secondary',
                    fontSize: muiTheme.typography.subtitle1.fontSize,
                    lineHeight: muiTheme.typography.subtitle1.lineHeight,
                    minWidth: 0,
                  })}
                >
                  {card.status}
                </Text>
                <Box
                  component={resolveStatusCardIcon(card.id)}
                  sx={{
                    fontSize: s('2xl'),
                    color: resolveStatusCardIconColor(card.statusTone),
                    justifySelf: 'end',
                    flexShrink: 0,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  pt: s('none'),
                  pb: hasDelta ? s('xs') : s('none'),
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: `calc(${s('sm')} + ${s('xs')})`,
                    pl: s('xs'),
                    minWidth: 0,
                  }}
                >
                  <Text role="sectionTitle">{card.count}</Text>
                  {card.percentage ? <MetricValueText>{card.percentage}</MetricValueText> : null}
                </Box>
                {!hasDelta && comparisonWindowLabel ? (
                  <Box
                    sx={{
                      pt: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      justifySelf: 'end',
                      textAlign: 'right',
                    }}
                  >
                    <MetricValueText nowrap>{comparisonWindowLabel}</MetricValueText>
                  </Box>
                ) : !hasDelta ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      justifySelf: 'end',
                      textAlign: 'right',
                    }}
                  >
                    <MetricValueText nowrap>No change</MetricValueText>
                  </Box>
                ) : null}
              </Box>
              {hasDelta ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'end',
                    columnGap: s('lg'),
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      flexWrap: 'wrap',
                      gap: s('sm'),
                      rowGap: s('xxs'),
                      pl: s('none'),
                      minWidth: 0,
                    }}
                  >
                    {renderStatusCardDeltaText(countDelta, resolvedDeltaTone)}
                    {renderStatusCardDeltaText(card.percentageDelta, resolvedDeltaTone, true)}
                  </Box>
                  {comparisonWindowLabel ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        alignSelf: 'end',
                        justifySelf: 'end',
                        minHeight: '100%',
                        textAlign: 'right',
                      }}
                    >
                      <MetricValueText nowrap>{comparisonWindowLabel}</MetricValueText>
                    </Box>
                  ) : (
                    <Box />
                  )}
                </Box>
              ) : null}
            </Box>
          </Surface>
        );
      })}
    </Box>
  );
};
